<template>
  <VDialogV3
    id="tender-create-card"
    position="bottom"
    :entity="aggregator?.sampleTender"
    title="Множественное создание"
    confirm-before-close
    :submit-disabled="isLoading"
    show-edit-button

    :loading="isLoading"

    @edit-button:clicked="showRequisitesDialog"
    @submit="onSubmit"
  >
    <template #badges>
      <VBadge v-if="aggregator.sampleTender?.isPreorderNeeded === true" class="default ellipsis">
        Предзаказ
      </VBadge>
      <VBadge v-if="aggregator.sampleTender?.winnersCategory?.id" class="default ellipsis">
        {{ aggregator.sampleTender.winnersCategory.name }}
      </VBadge>
      <VBadge v-if="aggregator.sampleTender?.isQtyOrderedFlexible === true" class="default">
        без обязательства выкупа фиксированного объёма
      </VBadge>
    </template>

    <header class="document-header">
      <dl>
        <dt>Потребность</dt>
        <dd v-if="aggregator.tenders.length > 1">{{ aggregator.tenders.length }} шт</dd>
        <dd v-else>
          <VLink class="ellipsis" @click="showDemandCard">
            {{ aggregator.sampleTender.product.name }}
          </VLink>
        </dd>

        <dt>Покупатель</dt>
        <dd class="ellipsis">{{ aggregator.sampleTender.buyerCompany?.name ?? 'Не назначен' }}</dd>
      </dl>

      <dl>
        <dt>Сумма и кол-во</dt>
        <dd v-if="aggregator.tenders.length > 1">--</dd>
        <dd v-else>
          {{ aggregator.sampleTender.qtyDesired.mul(aggregator.sampleTender.priceStartFull).format(aggregator.sampleTender.priceStartCurrencyCode) }}
          за {{ aggregator.sampleTender.qtyDesired?.format(aggregator.sampleTender.unit) || '--' }}
        </dd>

        <template v-if="aggregator.sampleTender.preorderShipmentPeriod">
          <dt>Период отгрузки</dt>
          <dd>{{ aggregator.sampleTender.preorderShipmentPeriod.toString({ prefix: 'с', middle: 'по' }) }}</dd>
        </template>
        <template v-else>
          <dt>Дата поставки</dt>
          <dd>{{ aggregator.sampleTender.desiredReceiptDate?.format() ?? '--' }}</dd>
        </template>
      </dl>

      <dl>
        <dt>Начало торгов</dt>
        <dd v-if="aggregator.sampleTender.isCreationDelayMode === true">
          {{ $core.liveTime.addMilliseconds(TendersService.config.CREATION_DELAY_MS) }}
        </dd>
        <dd v-else>{{ aggregator.sampleTender.startAt }}</dd>

        <dt>Конец торгов</dt>
        <dd>{{ aggregator.sampleTender.endAt || '--' }}</dd>
      </dl>

      <dl>
        <!--        <dt>Торги до</dt>-->
        <!--        <dd>{{ aggregator.limitDate ? $core.utils.date.format(aggregator.limitDate) : '&#45;&#45;' }}</dd>-->
        <dt>Пункт доставки</dt>
        <dd class="ellipsis">{{ aggregator.sampleTender.buyerStore?.name || aggregator.sampleTender.buyerStore?.address.stringRepresentation || 'Не выбран' }}</dd>

        <dt>Ответственный</dt>
        <dd class="ellipsis">{{ aggregator.sampleTender.responsibleBuyer?.name || 'Не назначен' }}</dd>
      </dl>

    </header>

    <header class="document-header requisites-time">
      <article>
        <header>Стартовая цена:</header>
        <span v-if="aggregator.tenders.length > 1" class="price">--</span>
        <span v-else class="price">
          {{ aggregator.sampleTender.priceStartFull.format(aggregator.sampleTender.priceStartCurrencyCode) }}
        </span>
      </article>
      <article>
        <header>До начала торгов</header>
        <!-- В режиме отложенного старта startAt будет посчитан только перед
             отправкой, поэтому обратный отсчёт показал бы время от момента
             создания сущности. Пишем саму задержку. -->
        <span v-if="aggregator.sampleTender.isCreationDelayMode === true">
          {{ $core.utils.date.toMinutes(TendersService.config.CREATION_DELAY_MS) }} минут
        </span>
        <span v-else>{{ aggregator.sampleTender.timeRemainingBeforeNextEvent }}</span>
      </article>
    </header>

    <VDataGridV2
      class="tenders"
      :data="aggregator.tenders"
      entity-type="tender-create"
      layout="fill"
      :use-filters-cache="false"
    >
      <VDataGridColumnV2
        id="id"
        content-align="center"
        width="94px"
      >
        <template #label>
          <div>Арт</div>
        </template>
        <template #default="{ row }">
          {{ row.product.code }}
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="name"
        sortable
        width="350px"
        content-class="ellipsis"
      >
        <template #label>
          <div>Сырьё</div>
          <div class="description">Производитель</div>
        </template>
        <template #default="{ row }">
          {{ row.product?.name || '--' }}

          <span
            v-if="row.manufacturersStringRepresentation !== null"
            class="description"
            :title="'Производители: ' + row.manufacturersStringRepresentation"
          >
            {{ row.manufacturersStringRepresentation }}
          </span>

          <VBadges>
            <VBadge v-if="row?.isPreorderNeeded === true">Предзаказ</VBadge>
            <VBadge v-if="failedTenders[row.product.id]" class="warning">
              Тендер не создан
            </VBadge>
          </VBadges>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="qty"
        content-align="right"
        width="150px"
        label="Кол-во"
        content-class="form"
      >
        <template #default="{ row }">
          <VInputNumberRusV2
            v-model="row.qtyDesired"
            :type="Qty"
            :unit="row.unit"
            required
            disabled
            autocomplete="off"
          />
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="price-full"
        content-align="right"
        width="180px"
        label="Стартовая цена"
        header-class="ellipsis"
        content-class="form"
      >
        <template #default="{ row }">
          <VInputNumberRusV2
            v-model="row.priceStartFull"
            :type="Price"
            required
            autocomplete="off"
            :unit="`${getCurrencySymbol(row)}/${row.unit?.name || '--'}`"
            :disabled="isTenderCreated(row) === true"
          />
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="currencyCode"
        content-align="left"
        width="130px"
        content-class="form"
      >
        <template #label>
          <div>Валюта</div>
          <VButton
            theme="link"
            class="description"
            @click="showChangeCurrencyPopup($event)"
          >
            Задать для всех
          </VButton>
        </template>
        <template #default="{ row }">
          <VSelectV2
            :model-value="getPriceStartCurrency(row)"
            option-label="symbol"
            option-description="description"
            :data="$core.currencies.currencies"
            @update:model-value="onPriceStartCurrency(row, $event)"
          />
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="startAt"
        label="Начало"
        width="150px"
        content-class="ellipsis"
      >
        <template #default>
          <template v-if="aggregator.sampleTender.isCreationDelayMode === true">
            {{ $core.liveTime.addMilliseconds(TendersService.config.CREATION_DELAY_MS).format('DD|MM|yyyy|HH|mm') }}
          </template>
          <template v-else>
            {{ aggregator.sampleTender.startAt.format('DD|MM|yyyy|HH|mm') }}
          </template>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="endAt"
        label="Конец"
        width="150px"
      >
        <template #default="{ row }">
          <div class="ellipsis">
            {{ aggregator.sampleTender.endAt?.format('DD|MM|yyyy|HH|mm') ?? '--' }}
          </div>

          <div class="description">
            <template v-if="row.preorderShipmentPeriod.isFilled()">
              Нужно к {{ row.preorderShipmentPeriod.start?.format() ?? '--' }}
            </template>
            <template v-else>
              Нужно к {{ row.desiredReceiptDate?.format() ?? '--' }}
            </template>
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        v-if="aggregator.tenders.length > 1"
        id="action"
        width="60px"
      >
        <template #default="{ row, index }">
          <VButton
            :disabled="isRemoveDisabled(row) === true"
            @click="aggregator.removeTenderByIndex(index)"
          >
            <template #icon>
              <VIcon material="delete" theme="outlined"/>
            </template>
          </VButton>
        </template>
      </VDataGridColumnV2>
    </VDataGridV2>
  </VDialogV3>
</template>

<style lang="scss" src="./TenderCreateCard.scss"/>

<script lang="ts">
import TendersAggregator from "@/domains/Tenders/TendersAggregator";
import TendersService    from '@/domains/Tenders/TendersService';
import Tender            from '@/domains/Tenders/Tender';
import Demand            from '@/domains/Demand/Demand';
import Price             from '@/core/types/Price';
import Qty               from '@/core/types/Qty';
import TimeStamp from "@/core/types/TimeStamp";

export default defineComponent({
  name: 'TenderCreateCard',

  props: {
    /** Потребности, из которых строится пачка тендеров. */
    demands: {
      type:    Array as PropType<Demand[]>,
      default: null,
    },
    /** Одна потребность — частный случай пачки, приходит из карточки потребности. */
    demand: {
      type:    Object as PropType<Demand>,
      default: null,
    },
    /**
     * Количество в тендере ориентировочное — покупатель может заказать
     * больше или меньше. Меняет категорию победителей и снимает дату
     * поставки.
     */
    isFlexible: {
      type:    Boolean,
      default: false,
    },
  },

  emits: ['tender:created'],

  setup() {
    return {
      TendersService,
      Price,
      Qty,
      TimeStamp,
    };
  },

  data() {
    return {
      aggregator: undefined as unknown as TendersAggregator,
      /** Тендеры, которые не создались — помечаются в таблице */
      failedTenders: {},
      isLoading:     false,
    };
  },

  created() {
    this.init();
  },

  // mounted() {
  //   this.showRequisitesDialog();
  // },

  computed: {
  },

  methods: {
    /**
     * Пачка тендеров из потребностей. Принимаем и одну потребность, и список:
     * одиночное создание — частный случай массового, отдельной ветки под него
     * дальше по карточке нет.
     */
    async init(): void {
      let sourceDemands: Demand[];

      if (this.$core.utils.isNotEmptyArray(this.demands) === true) {
        sourceDemands = this.demands;
      } else {
        sourceDemands = [this.demand];
      }

      const tenders = sourceDemands.map((demand) => {
        if (this.isFlexible === true) {
          return Tender.fromDemandWithFlexibleQty(demand);
        } else {
          return Tender.fromDemand(demand);
        }
      });

      this.aggregator = new TendersAggregator(tenders);
      await this.aggregator.initPrices();

      this.showRequisitesDialog();
    },

    /**
     * Реквизиты заполняются на образце — первом тендере пачки. Значения
     * раскладываются по остальным перед отправкой, а не по мере ввода:
     * иначе на сотне тендеров каждое нажатие клавиши даёт сотню записей.
     */
    async showRequisitesDialog(): Promise<void> {
      await this.$core.dialogs.TenderRequisites.showAsPromise(
        {
          tender: this.aggregator.sampleTender,
        },
        {
          /** Форма вернула заполненный клон — он и становится образцом,
           *  по которому равняется пачка перед отправкой. */
          updated: (tender: Tender) => {
            this.aggregator.tenders[0] = tender;
          },
        },
      );
    },

    /** Тендер уже создан на предыдущей попытке — редактировать его нечего. */
    isTenderCreated(tender: Tender): boolean {
      if (Object.keys(this.failedTenders).length === 0) {
        return false;
      } else {
        return this.failedTenders[tender.product.id] === undefined;
      }
    },

    /** Последний тендер из пачки удалить нельзя, как и уже созданный. */
    isRemoveDisabled(tender: Tender): boolean {
      return this.aggregator.tenders.length < 2 || this.isTenderCreated(tender) === true;
    },

    getPriceStartCurrency(tender: Tender) {
      return this.$core.utils.money.getCurrencyByCode(tender.priceStartCurrencyCode);
    },

    getCurrencySymbol(tender: Tender): string {
      if (!tender.priceStartCurrencyCode) {
        return '';
      } else {
        return this.$core.utils.money.getCurrencyByCode(tender.priceStartCurrencyCode).symbol;
      }
    },

    onPriceStartCurrency(tender: Tender, currency): void {
      tender.priceStartCurrencyCode = currency.code;
    },

    /** Массовая смена валюты: одно значение на всю пачку. */
    showChangeCurrencyPopup($event: MouseEvent): void {
      this.$core.dialogs.TenderChangeCurrencyPopup.hideIfOpen();
      this.$core.dialogs.TenderChangeCurrencyPopup.show(
        { target: $event.target },
        {
          save: (currency) => {
            this.aggregator.tenders.forEach((tender) => {
              tender.priceStartCurrencyCode = currency.code;
            });
          },
        },
      );
    },

    async onSubmit(): Promise<void> {
      this.isLoading = true;
      this.failedTenders = {};

      /** Сначала раскладка общих полей, потом задержка старта: она пишет в
       *  startAt, и обратный порядок затёр бы её значением из формы. */
      this.aggregator.tenders.forEach((tender) => {
        tender.applySharedFieldsFrom(this.aggregator.sampleTender);

        if (tender.isCreationDelayMode === true) {
          tender.applyCreationDelay();
        }
      });

      const promises = this.aggregator.tenders.map(tender => TendersService.add(tender));
      const response = await Promise.allSettled(promises);
      const { successTenders, failedTenders } = this.parseTenderResult(response);

      this.isLoading = false;

      this.printTenderResult(successTenders, failedTenders);

      /** Если все тендеры успешно создались */
      if (this.$core.utils.isNotEmptyArray(failedTenders) === false) {
        this.$emit('tender:created');
        this.$core.dialogs.TenderCreateCard.resolve();
      }
    },

    parseTenderResult(response) {
      const successTenders = [];
      const failedTenders  = [];

      response.forEach(({ status, value }, index) => {
        const tender = this.aggregator.tenders[index];

        if (status === 'fulfilled' && value) {
          successTenders.push(tender);
        } else {
          failedTenders.push(tender);
          this.failedTenders[tender.product.id] = tender;
        }
      });

      return { successTenders, failedTenders };
    },

    printTenderResult(successTenders, failedTenders): void {
      if (successTenders.length > 0) {
        this.$core.showNotification.success(
          `Успешно создано ${successTenders.length} ${this.$core.utils.regional.ru.pluralize(successTenders.length, 'тендер', 'тендера', 'тендеров')}`,
        );
      }

      if (failedTenders.length > 0) {
        const names = failedTenders.map(tender => tender.product.name);

        this.$core.showNotification.error(
          `Не получилось создать ${failedTenders.length} ${this.$core.utils.regional.ru.pluralize(failedTenders.length, 'тендер', 'тендера', 'тендеров')}: ${names.join(', ')}`,
        );
      }
    },

    showDemandCard(): void {
      this.$core.dialogs.DemandCard.show({
        id: this.aggregator.sampleTender.demand.id
      })
    },
  },
});
</script>
